import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoMeta from "../components/storefront/SeoMeta";
import ProductFilters, { clearProductFilters, getProductFilterQuery } from "../components/storefront/ProductFilters";
import ProductListingShell, { ProductListingResults } from "../components/storefront/ProductListingShell";
import { listingHeroImage } from "../components/storefront/assets";
import { useI18n } from "../lib/i18n";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { buildListingJsonLd } from "../lib/listingJsonLd";
import Pagination, { PAGE_SIZE } from "../components/storefront/Pagination";
import CategorySubnav from "../components/storefront/CategorySubnav";
import { getCategories } from "../lib/publicConfig";
import { pickLocale } from "../lib/format";

/**
 * Bölmədə məhsulu olan KÖK kateqoriyalar, ağac sırası ilə — /endirim
 * səhifəsinin başlığı altındakı «Hamısı | Trikotaj | Şalvarlar» sətri.
 * Facet sayları birbaşa təyinata görədir (alt kateqoriya), ona görə köklərə
 * toplanır; scope (on_sale) facet sorğusunda da durur, beləliklə endirimsiz
 * məhsulu olan kateqoriya bu sətrə heç vaxt düşmür.
 */
function rootsWithStock(facetCategories, rows) {
  const byHandle = new Map(rows.map((row) => [row.handle, row]));
  const byId = new Map(rows.map((row) => [row.id, row]));
  const totals = new Map();
  (facetCategories || []).forEach((facet) => {
    let node = byHandle.get(facet.value);
    const seen = new Set();
    while (node?.parent_id && byId.has(node.parent_id) && !seen.has(node.id)) {
      seen.add(node.id);
      node = byId.get(node.parent_id);
    }
    if (!node || Number(facet.count || 0) <= 0) return;
    totals.set(node.handle, (totals.get(node.handle) || 0) + Number(facet.count || 0));
  });
  return rows
    .filter((row) => !row.parent_id && totals.has(row.handle))
    .map((row) => ({ ...row, scoped_count: totals.get(row.handle) }));
}

/**
 * A listing whose scope comes from the route rather than from a filter.
 *
 * OUTLET and Endirim are the same page as `/shop` with one query parameter
 * pinned, so they share this rather than duplicating the fetch/filter/paginate
 * wiring twice more. The scope travels in `scopeParams`, not in the URL, which
 * is what keeps the filter sheet's "Göstər (N)" preview honest — it counts
 * within the shelf instead of across the whole catalogue.
 */
export default function ScopedListing({
  scope,
  saleOnly = false,
  path,
  eyebrow,
  title,
  description,
  emptyTitle,
  emptyDescription,
  listName,
  heroIndex = 0,
  testId,
  /* Başlığın altında bölmədə məhsulu olan kök kateqoriyaların link sətri. */
  categoryLinks = false,
}) {
  const { locale, t } = useI18n();
  useDocumentTitle(title);
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [sectionRoots, setSectionRoots] = useState([]);

  useEffect(() => {
    if (!categoryLinks) return undefined;
    let active = true;
    const facetParams = {};
    if (scope?.outlet) facetParams.outlet = true;
    if (scope?.on_sale) facetParams.on_sale = true;
    Promise.all([api.get("/products/facets", { params: facetParams }), getCategories()])
      .then(([facets, rows]) => {
        if (!active) return;
        setSectionRoots(rootsWithStock(facets.data?.categories, Array.isArray(rows) ? rows : []));
      })
      .catch(() => { if (active) setSectionRoots([]); });
    return () => { active = false; };
  }, [categoryLinks, scope]);

  const activeCategory = sp.get("category") || "";
  const linkTo = (handle) => {
    const next = new URLSearchParams();
    if (sp.get("sort")) next.set("sort", sp.get("sort"));
    if (handle) next.set("category", handle);
    const search = next.toString();
    return `${path}${search ? `?${search}` : ""}`;
  };
  const subnav = categoryLinks && sectionRoots.length > 0 ? (
    <CategorySubnav
      items={sectionRoots.map((row) => ({
        key: row.handle,
        label: pickLocale(row.title, locale),
        to: linkTo(row.handle),
        active: activeCategory === row.handle,
        count: row.scoped_count,
      }))}
      allTo={linkTo("")}
      allCount={sectionRoots.reduce((sum, row) => sum + Number(row.scoped_count || 0), 0)}
      allActive={!activeCategory}
      testId={`${testId}-subnav`}
    />
  ) : null;

  const params = useMemo(() => ({
    ...scope,
    sort: sp.get("sort") || "newest",
    ...getProductFilterQuery(sp),
    page: Math.max(1, Number(sp.get("page")) || 1),
    page_size: PAGE_SIZE,
  }), [sp, scope]);

  /* A backend that predates the sale filter ignores `on_sale` rather than
     rejecting it, so this shelf would quietly render the entire catalogue. The
     tell is `compare_at_minor`: it ships alongside the filter, so if no item
     carries the field the server did not do the filtering and this page must
     not pretend otherwise. Once the field is present the server is trusted and
     paging works normally. */
  const serverFiltersSale = products.some((item) => "compare_at_minor" in item);
  const visible = saleOnly && !serverFiltersSale
    ? products.filter((item) => Number(item.compare_at_minor || 0) > Number(item.min_price || 0))
    : products;
  const trustedTotal = saleOnly && !serverFiltersSale ? visible.length : total;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    api.get("/products", { params })
      .then((r) => {
        if (!active) return;
        const items = r.data.items || [];
        setProducts(items);
        setTotal(Number(r.data.total ?? items.length));
      })
      .catch(() => {
        if (!active) return;
        setProducts([]);
        setTotal(0);
        setLoadError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params, retryKey]);

  return (
    <div className="bg-[var(--adf-cream)] text-[var(--adf-ink)]" data-testid={testId}>
      <SeoMeta
        title={title}
        description={description}
        locale={locale}
        canonicalPath={path}
        /* An empty shelf is a thin page; do not invite it into the index. */
        seo={visible.length ? {} : { index: false }}
        jsonLd={buildListingJsonLd({
          kind: "collection",
          basePath: path,
          handle: "",
          title,
          description,
          products: visible,
          locale,
          label: locale === "az" ? "Kataloq" : "Shop",
        })}
      />
      <Header />
      <ProductListingShell
        title={title}
        eyebrow={eyebrow}
        description={description}
        image={listingHeroImage(heroIndex)}
        subnav={subnav}
        total={trustedTotal}
        loading={loading}
        toolbar={(
          <ProductFilters
            searchParams={sp}
            setSearchParams={setSp}
            total={trustedTotal}
            loading={loading}
            scopeParams={scope}
          />
        )}
        testId={`${testId}-listing`}
      >
        <ProductListingResults
          listName={listName}
          products={visible}
          loading={loading}
          loadError={loadError}
          onRetry={() => setRetryKey((value) => value + 1)}
          onClear={() => clearProductFilters(sp, setSp)}
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
          emptyActions={(
            <>
              <Link to="/shop" className="adf-cta adf-cta-outline">{t("cta.shop", "Kataloqa bax")}</Link>
            </>
          )}
          productTestId={`${testId}-product`}
        />

        <Pagination
          page={params.page}
          total={trustedTotal}
          onChange={(next) => {
            const spNext = new URLSearchParams(sp);
            if (next <= 1) spNext.delete("page"); else spNext.set("page", String(next));
            setSp(spNext);
            document.getElementById("main")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          testId={`${testId}-pagination`}
        />
      </ProductListingShell>
      <Footer />
    </div>
  );
}
